import { useEffect, useState } from "react";
import { PreloadLink as Link } from "@/components/PreloadLink";
import { Phone, Mail, MapPin, Clock, Linkedin, Instagram, Youtube, ChevronDown } from "lucide-react";
import {
  logoOrBrasilDarkSvg as footerLogo,
  seloIso9001Img as isoLogo,
} from "@/lib/r2-assets";
import { productLines, productFamilies, services, isHydratedFromCache, subscribeToData } from "@/lib/data";
import { useDataVersion } from "@/hooks/useDataVersion";
import { whatsappContactUrl } from "@/lib/whatsapp";

const WhatsAppIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const FacebookIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
  </svg>
);

/** Collapsible section — collapsed on mobile, always open on md+.
 *  When `to` is provided, the desktop heading becomes a link to that
 *  route (e.g. footer "Fixadores" → /produtos/fixadores). Mobile keeps
 *  the heading as a tap-to-toggle button — tapping should expand, not
 *  navigate. */
const FooterSection = ({
  title,
  to,
  children,
  openSection,
  sectionKey,
  toggle,
}: {
  title: string;
  to?: string;
  children: React.ReactNode;
  openSection: string | null;
  sectionKey: string;
  toggle: (key: string) => void;
}) => {
  const isOpen = openSection === sectionKey;
  return (
    <div className="border-b border-surface-dark-elevated md:border-0 md:mb-8 last:md:mb-0">
      <button
        onClick={() => toggle(sectionKey)}
        className="flex w-full items-center justify-between py-3 md:hidden"
        aria-expanded={isOpen}
      >
        <h3 className="text-base font-bold text-white">{title}</h3>
        <ChevronDown className={`w-4 h-4 text-on-surface-dark-subtle transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`} />
      </button>
      <h3 className="hidden md:block text-lg font-bold text-white mb-4">
        {to ? (
          <Link to={to} className="hover:text-primary transition-colors">
            {title}
          </Link>
        ) : (
          title
        )}
      </h3>
      <div
        className={`overflow-hidden transition-all duration-300 md:!max-h-none md:!opacity-100 md:!pb-0 ${
          isOpen ? "max-h-96 opacity-100 pb-3" : "max-h-0 opacity-0"
        }`}
      >
        {children}
      </div>
    </div>
  );
};

const Footer = () => {
  // Footer stays mounted for the whole visit (it lives in the router's site
  // shell, see routes-client.tsx), so a remount no longer re-reads the CMS
  // data: re-render when the live fetch replaces the build-time snapshot.
  useDataVersion();
  const [openSection, setOpenSection] = useState<string | null>(null);
  // Honest "cached" marker — re-renders when the data layer toggles
  // its hydrated-from-cache flag (cache hit on mount, then live fetch
  // replaces it). Avoids the misleading static "cached" label.
  const [hydratedFromCache, setHydratedFromCache] = useState(() => isHydratedFromCache());
  useEffect(() => {
    return subscribeToData(() => setHydratedFromCache(isHydratedFromCache()));
  }, []);

  const toggle = (key: string) => {
    setOpenSection((prev) => (prev === key ? null : key));
  };

  // Build dynamic product columns from CMS data (exclude "servicos").
  // Drop columns with zero published families so the footer doesn't
  // show a header above an empty list during pre-launch.
  // `to` makes the column heading itself a link to the line index page
  // (e.g. /produtos/fixadores); the items below remain individual
  // links to their respective families.
  const productColumns = productLines
    .filter(l => l.slug !== "servicos")
    .map(line => ({
      title: line.name,
      to: `/produtos/${line.slug}`,
      links: productFamilies
        .filter(f => f.lineId === line.slug)
        .map(f => ({ to: `/produtos/${line.slug}/${f.slug}`, label: f.name })),
    }))
    .filter(col => col.links.length > 0);

  // Services from CMS — empty array when nothing is published. The
  // Serviços footer column is suppressed in that case (see render below).
  const serviceLinks = Object.values(services).map(s => ({
    to: `/servicos/${s.slug}`,
    label: s.name,
  }));

  return (
    <footer className="bg-surface-dark text-on-surface-dark-muted relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-primary/5 rounded-full blur-[150px]" aria-hidden="true" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-primary/5 rounded-full blur-[150px]" aria-hidden="true" />

      {/* Main Footer */}
      <div className="container-industrial py-10 md:py-16 relative z-10">
        {/* lg: first column reserves min 290px so the 5 social icons
            (5 × 44px target + 4 × 12px gap = 268px) never wrap onto a
            second row in the company column. The other 3 columns
            share the remainder as 1fr each, so they shrink to absorb
            the difference at narrower lg widths. */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-[minmax(290px,1.2fr)_1fr_1fr_1fr] gap-0 md:gap-12">
          {/* Company Info — always visible */}
          <div className="pb-6 md:pb-0">
            <div className="mb-4 md:mb-6">
              <img
                src={footerLogo}
                alt="Or Brasil Fixação"
                className="h-10 md:h-12 w-auto"
                width={120}
                height={48}
                loading="lazy"
              />
            </div>
            <p className="text-on-surface-dark-muted text-sm leading-relaxed mb-3 md:mb-4">
              Desde 1998, fornecendo soluções completas em fixação industrial para os mais diversos segmentos.
              Certificação ISO 9001:2015.
            </p>
            <img src={isoLogo} alt="Certificação ISO 9001:2015" className="h-14 md:h-16 w-auto aspect-[60/64] mb-4 md:mb-6" width={60} height={64} loading="lazy" decoding="async" />
            <div className="flex gap-3 flex-wrap">
              <a href={whatsappContactUrl()} target="_blank" rel="noopener noreferrer" aria-label="WhatsApp" className="w-11 h-11 bg-surface-dark-elevated rounded-xl flex items-center justify-center hover:bg-primary hover:text-white transition-all duration-300">
                <WhatsAppIcon className="w-5 h-5" />
              </a>
              <a href="https://www.instagram.com/orbrasil_fixacao" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="w-11 h-11 bg-surface-dark-elevated rounded-xl flex items-center justify-center hover:bg-primary hover:text-white transition-all duration-300">
                <Instagram className="w-5 h-5" />
              </a>
              <a href="https://www.youtube.com/@orbrasil_oficial" target="_blank" rel="noopener noreferrer" aria-label="YouTube" className="w-11 h-11 bg-surface-dark-elevated rounded-xl flex items-center justify-center hover:bg-primary hover:text-white transition-all duration-300">
                <Youtube className="w-5 h-5" />
              </a>
              <a href="https://www.linkedin.com/company/orbrasil/" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" className="w-11 h-11 bg-surface-dark-elevated rounded-xl flex items-center justify-center hover:bg-primary hover:text-white transition-all duration-300">
                <Linkedin className="w-5 h-5" />
              </a>
              <a href="https://www.facebook.com/orbrasilfixacao" target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="w-11 h-11 bg-surface-dark-elevated rounded-xl flex items-center justify-center hover:bg-primary hover:text-white transition-all duration-300">
                <FacebookIcon className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Products — collapsible on mobile */}
          <nav aria-label="Produtos">
            {productColumns.map((col) => (
              <FooterSection
                key={col.title}
                title={col.title}
                to={col.to}
                sectionKey={col.title}
                openSection={openSection}
                toggle={toggle}
              >
                <ul className="space-y-2 md:space-y-3">
                  {col.links.map(link => (
                    <li key={link.to}>
                      <Link to={link.to} className="text-on-surface-dark-muted hover:text-primary transition-colors text-sm">
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </FooterSection>
            ))}
          </nav>

          {/* Serviços & Sobre — collapsible on mobile. Serviços column
              is suppressed entirely when the CMS has no published
              services, so the footer never shows an empty list under
              the header. The "Sobre" heading links to /sobre; the
              previous "Empresa" label was renamed to match the main
              nav (which also says "Sobre"). */}
          <nav aria-label="Serviços e Sobre">
            {serviceLinks.length > 0 && (
              <FooterSection title="Serviços" to="/servicos" sectionKey="servicos" openSection={openSection} toggle={toggle}>
                <ul className="space-y-2 md:space-y-3">
                  {serviceLinks.map(s => (
                    <li key={s.to}>
                      <Link to={s.to} className="text-on-surface-dark-muted hover:text-primary transition-colors text-sm">
                        {s.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </FooterSection>
            )}

            <FooterSection title="Sobre" to="/sobre" sectionKey="sobre" openSection={openSection} toggle={toggle}>
              <ul className="space-y-2 md:space-y-3">
                <li><Link to="/sobre" className="text-on-surface-dark-muted hover:text-primary transition-colors text-sm">Sobre a Or Brasil</Link></li>
                <li><Link to="/eventos" className="text-on-surface-dark-muted hover:text-primary transition-colors text-sm">Eventos</Link></li>
                <li><Link to="/contato" className="text-on-surface-dark-muted hover:text-primary transition-colors text-sm">Fale Conosco</Link></li>
              </ul>
            </FooterSection>
          </nav>

          {/* Contact — collapsible on mobile */}
          <div>
            <FooterSection title="Contato" sectionKey="contato" openSection={openSection} toggle={toggle}>
              <ul className="space-y-3 md:space-y-4">
                <li className="flex items-start gap-3">
                  <a href="tel:+551138037000" aria-label="Telefone" className="w-11 h-11 bg-surface-dark-elevated rounded-xl flex items-center justify-center shrink-0 hover:bg-primary hover:text-white transition-all duration-300">
                    <Phone className="w-5 h-5 text-white" />
                  </a>
                  <a href="tel:+551138037000" className="text-sm text-on-surface-dark-muted hover:text-primary transition-colors self-center">
                    +55 (11) 3803-7000
                  </a>
                </li>
                <li className="flex items-start gap-3">
                  <a href={whatsappContactUrl()} target="_blank" rel="noopener noreferrer" aria-label="WhatsApp" className="w-11 h-11 bg-surface-dark-elevated rounded-xl flex items-center justify-center shrink-0 hover:bg-primary hover:text-white transition-all duration-300">
                    <WhatsAppIcon className="w-5 h-5 text-white" />
                  </a>
                  <a href={whatsappContactUrl()} target="_blank" rel="noopener noreferrer" className="text-sm text-on-surface-dark-muted hover:text-primary transition-colors self-center">
                    +55 (11) 97227-3199
                  </a>
                </li>
                <li className="flex items-start gap-3">
                  <a href="mailto:orbrasil@orbrasil.com.br" aria-label="E-mail" className="w-11 h-11 bg-surface-dark-elevated rounded-xl flex items-center justify-center shrink-0 hover:bg-primary hover:text-white transition-all duration-300">
                    <Mail className="w-5 h-5 text-white" />
                  </a>
                  <a href="mailto:orbrasil@orbrasil.com.br" className="text-sm text-on-surface-dark-muted hover:text-primary transition-colors self-center">
                    orbrasil@orbrasil.com.br
                  </a>
                </li>
                <li className="flex items-start gap-3">
                  <a href="https://maps.google.com/?q=Rua Dr. Rubens Meireles 148, Barra Funda, São Paulo" target="_blank" rel="noopener noreferrer" aria-label="Endereço no Google Maps" className="w-11 h-11 bg-surface-dark-elevated rounded-xl flex items-center justify-center shrink-0 hover:bg-primary hover:text-white transition-all duration-300">
                    <MapPin className="w-5 h-5 text-white" />
                  </a>
                  <a href="https://maps.google.com/?q=Rua Dr. Rubens Meireles 148, Barra Funda, São Paulo" target="_blank" rel="noopener noreferrer" className="text-sm text-on-surface-dark-muted hover:text-primary transition-colors self-center">
                    Rua Dr. Rubens Meireles, 148<br />
                    Barra Funda – São Paulo – SP<br />
                    Cep: 01141-000
                  </a>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-11 h-11 bg-surface-dark-elevated rounded-xl flex items-center justify-center shrink-0">
                    <Clock className="w-5 h-5 text-white" />
                  </div>
                  <div className="self-center text-right text-sm text-on-surface-dark-muted">
                    <p>Seg - Qui: 8h às 18h</p>
                    <p>Sex: 8h às 17h</p>
                  </div>
                </li>
              </ul>
            </FooterSection>
          </div>
        </div>
      </div>

      {/* Bottom Footer */}
      <div className="border-t border-surface-dark-elevated relative z-10">
        <div className="container-industrial py-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-sm text-on-surface-dark-subtle">
            {/* Year is derived from __BUILD_STAMP__ (set in vite.config.ts
                as `${date}-${time}` like "2026-04-15-1930") so SSR and
                client render the SAME value. `new Date().getFullYear()`
                returns the year at PAINT time, which is identical to
                build time within a calendar year, but diverges if a
                single deploy spans Dec 31→Jan 1 — and React's
                hydrateRoot flags that mismatch. The build-stamp path
                also keeps the copyright clearly tied to the latest
                deploy, which is the more useful signal for visitors. */}
            © {__BUILD_STAMP__.slice(0, 4)} Or Brasil. Todos os direitos reservados.
          </p>
          <div className="flex items-center gap-6 text-sm text-on-surface-dark-subtle">
            <Link to="/privacidade" className="hover:text-primary transition-colors">
              Política de Privacidade
            </Link>
            <Link to="/termos" className="hover:text-primary transition-colors">
              Termos de Uso
            </Link>
            <span className="text-on-surface-dark-subtle text-xs">
              {/* Build stamp + "cached" marker (15C). The "cached"
                  suffix is a private ops tell — appears only when the
                  current page actually hydrated from localStorage
                  (vs a fresh CMS fetch). When ops sees "cached," they
                  know the user might be looking at a stale snapshot
                  from a previous visit and the live state will arrive
                  shortly via the background refresh.
                  Bumped from -dark-faint (HSL L47%) to -dark-subtle
                  (HSL L65%) — Lighthouse Accessibility flagged the
                  contrast on the build stamp at text-xs / dark
                  background as below 4.5:1. -subtle clears AAA. */}
              {__BUILD_STAMP__}-{__BUILD_HASH__}
              {hydratedFromCache ? " cached" : ""}
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
